import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useEffect,
  useCallback
} from 'react';
import { useAuth } from './AuthContext';

interface OrganizationContextType {
  selectedOrgId: string | null;
  setSelectedOrgId: (orgId: string | null) => void;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

interface OrganizationProviderProps {
  children: ReactNode;
}

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const [selectedOrgIdState, setSelectedOrgIdState] = useState<string | null>(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    const storedValue = window.localStorage.getItem('selectedOrgId');
    return storedValue || null;
  });
  const { isSuperAdmin, user, isLoading } = useAuth();

  const clearSelectedOrgId = useCallback(() => {
    setSelectedOrgIdState(null);

    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.removeItem('selectedOrgId');
  }, []);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!isSuperAdmin || !user) {
      clearSelectedOrgId();
    }
  }, [isLoading, isSuperAdmin, user, clearSelectedOrgId]);

  const contextValue = useMemo(() => {
    const setSelectedOrgId = (orgId: string | null) => {
      setSelectedOrgIdState(orgId);

      if (typeof window === 'undefined') {
        return;
      }

      if (orgId) {
        window.localStorage.setItem('selectedOrgId', orgId);
      } else {
        window.localStorage.removeItem('selectedOrgId');
      }
    };

    return {
      selectedOrgId: selectedOrgIdState,
      setSelectedOrgId
    };
  }, [selectedOrgIdState]);

  return (
    <OrganizationContext.Provider value={contextValue}>
      {children}
    </OrganizationContext.Provider>
  );
};

export default OrganizationContext;

